import React, { useState, useEffect } from 'react';
import { getParentescoById, createParentesco, updateParentesco } from '../services/parentescoService';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, X, ArrowLeft, Users, RefreshCw } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

export default function ParentescoForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { token } = useAuth();
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        nombre: '',
        activo: true
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isEditMode) {
            cargarParentesco();
        }
    }, [id]);

    const cargarParentesco = async () => {
        try {
            setLoading(true);
            const data = await getParentescoById(token, id);
            setFormData({
                nombre: data.nombre,
                activo: data.activo
            });
        } catch (error) {
            console.error('Error al cargar el parentesco:', error);
            toast.error('Error al cargar los datos del parentesco');
            navigate('/parametrizacion/parentescos');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.nombre.trim()) {
            toast.error('El nombre del parentesco es requerido');
            return;
        }

        try {
            setLoading(true);
            if (isEditMode) {
                await updateParentesco(token, id, formData);
                toast.success('Parentesco actualizado exitosamente');
            } else {
                await createParentesco(token, formData);
                toast.success('Parentesco creado exitosamente');
            }
            setTimeout(() => navigate('/parametrizacion/parentescos'), 1000);
        } catch (error) {
            console.error('Error al guardar:', error);
            toast.error(error.response?.data?.message || 'Error al guardar el parentesco');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto bg-white shadow-sm border border-gray-200 mb-6">
            <Toaster position="top-right" />

            {/* Header */}
            <div className="bg-slate-700 p-3 flex items-center gap-3 text-white">
                <button
                    onClick={() => navigate('/parametrizacion/parentescos')}
                    className="p-1 hover:bg-slate-600 rounded transition-colors text-slate-300 hover:text-white"
                    title="Volver a la lista"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-slate-300" />
                    <h1 className="text-lg font-semibold tracking-tight">
                        {isEditMode ? 'Mantenimiento: Editar Parentesco' : 'Mantenimiento: Nuevo Parentesco'}
                    </h1>
                </div>
            </div>

            {/* Form */}
            <div className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">
                            Nombre del Parentesco <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            required
                            maxLength="150"
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                            placeholder="Ej: Padre, Madre, Hijo(a)"
                            value={formData.nombre}
                            onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                        />
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-sm border border-gray-200">
                        <div>
                            <p className="font-bold text-sm text-gray-700 uppercase">Estado del Parentesco</p>
                            <p className="text-xs text-gray-500 mt-1">
                                {formData.activo ? 'El parentesco está disponible para ser seleccionado.' : 'El parentesco no aparecerá en las listas de selección.'}
                            </p>
                        </div>
                        <label className="flex items-center cursor-pointer">
                            <div className="relative">
                                <input
                                    type="checkbox"
                                    className="sr-only"
                                    checked={formData.activo}
                                    onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                                />
                                <div className={`block w-12 h-6 rounded-full transition-colors ${formData.activo ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
                                <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${formData.activo ? 'transform translate-x-6' : ''}`}></div>
                            </div>
                        </label>
                    </div>

                    <div className="pt-4 border-t border-gray-200 flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={() => navigate('/parametrizacion/parentescos')}
                            className="px-4 py-1.5 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-100 transition-colors flex items-center text-sm font-medium"
                        >
                            <X className="w-3.5 h-3.5 mr-1.5" />
                            CANCELAR
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-1.5 bg-blue-600 text-white rounded-sm hover:bg-blue-700 transition-colors flex items-center text-sm font-medium disabled:opacity-70 shadow-sm"
                        >
                            {loading ? (
                                <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                            ) : (
                                <Save className="w-3.5 h-3.5 mr-1.5" />
                            )}
                            {isEditMode ? 'GUARDAR CAMBIOS' : 'CREAR PARENTESCO'}
                        </button>
                    </div>

                </form>
            </div>
        </div>
    );
}
